import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { Alert, Button, ButtonWithSpinner, Modal } from '@reveni-io/reveni-ui'

import { userPermissions } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'
import ExchangeOrderAsDraftSelector from '@/returns/components/ExchangeOrderAsDraftSelector/ExchangeOrderAsDraftSelector'
import IssueRefundSelector from '@/returns/components/IssueRefundSelector/IssueRefundSelector'
import ManageRestock from '@/returns/components/ManageRestock/ManageRestock'
import { refundStatuses, statuses, transactionTypes } from '@/returns/constants'
import useIssueExchangeOrderProcessing from '@/returns/hooks/useIssueExchangeOrderProcessing/useIssueExchangeOrderProcessing'
import useIssueRefund from '@/returns/hooks/useIssueRefund/useIssueRefund'
import useRestock from '@/returns/hooks/useRestock/useRestock'

import InfoMessage from './InfoMessage/InfoMessage'
import * as Styles from './styles'

const AcceptMultipleRefundsModal = ({
  show,
  onAccept,
  onClose,
  allowManageRestock,
  allowIssueRefund,
  refundQuantity,
  allowsExchangeOrderProcessing,
  ordersWithRemainingAmount,
  ordersWithRequestedProducts,
  currency,
  accepting,
  notUsableOrders,
  selectedOrders,
  issueRefundAtStatus,
}) => {
  const { t } = useTranslation()
  const { info } = useContext(AppContext)
  const { hasPermission } = useUserPermissions()
  const { restock, setRestock, restockLocation, setRestockLocation, restockIsValid, inventoryLocations } = useRestock(
    info?.selectedStore,
    allowManageRestock,
    info?.userInfo?.id
  )

  const canEditIssueRefund = hasPermission(userPermissions.editIssueRefund, info?.selectedStore?.id)

  const [issueRefund, setIssueRefund] = useIssueRefund(
    allowIssueRefund,
    refundQuantity,
    issueRefundAtStatus,
    canEditIssueRefund
  )

  const { issueExchangeOrderProcessing, setIssueExchangeOrderProcessing } = useIssueExchangeOrderProcessing(
    info?.selectedStore,
    allowsExchangeOrderProcessing,
    transactionTypes.exchange
  )

  const isFormValid = () => {
    if (allowManageRestock) {
      return restockIsValid()
    }

    return true
  }

  return (
    <Modal
      show={show}
      onClose={onClose}
      size="l"
      actions={
        <Styles.ActionsContainer>
          <Button variant="outlined" onClick={onClose}>
            {t('Cancelar')}
          </Button>
          <ButtonWithSpinner
            onClick={() =>
              onAccept({
                issueRefund,
                restock,
                restockLocation,
                issueExchangeOrderProcessing,
                status: statuses.accepted,
              })
            }
            loading={accepting}
            loadingLabel={t('Confirmando cambios')}
            defaultLabel={t('Confirmar cambios')}
            disabled={accepting || !isFormValid()}
          />
        </Styles.ActionsContainer>
      }
      title={t('Aceptar productos')}
    >
      <InfoMessage notUsableOrders={notUsableOrders} selectedOrders={selectedOrders} />
      <Styles.OptionsContainer>
        {allowManageRestock && (
          <ManageRestock
            value={restock}
            onChange={value => setRestock(value)}
            locations={inventoryLocations}
            selectedLocation={restockLocation}
            onChangeLocation={e => setRestockLocation(e.target.value)}
            provider={info?.selectedStore?.provider}
            storeId={info?.selectedStore?.id}
          />
        )}
        {Number(ordersWithRemainingAmount) > 0 && (
          <IssueRefundSelector
            ordersWithRemainingAmount={ordersWithRemainingAmount}
            onChange={value => setIssueRefund(value)}
            checked={issueRefund}
            allowIssueRefund={allowIssueRefund}
            canEditIssueRefund={canEditIssueRefund}
            currency={currency}
            refundStatus={refundStatuses.pending}
          />
        )}
        {allowsExchangeOrderProcessing && (
          <ExchangeOrderAsDraftSelector
            ordersWithRequestedProducts={ordersWithRequestedProducts}
            checked={issueExchangeOrderProcessing}
            onChange={setIssueExchangeOrderProcessing}
          />
        )}
      </Styles.OptionsContainer>
      <Styles.AlertContainer>
        <Alert type="warning">{t('Esta acción no se podrá deshacer. ¿Quieres continuar?')}</Alert>
      </Styles.AlertContainer>
    </Modal>
  )
}

export default AcceptMultipleRefundsModal
